import { startOfNextFestivalDay } from './festival-time.ts'

type Timed = { id: string; date: Date | string }

const time = (date: Date | string) => new Date(date).getTime()

export type FestivalPhase<T extends Timed> =
	| { phase: 'empty' }
	/** before the first concert */
	| { phase: 'before'; first: T; next: T }
	/**
	 * from the first concert until the evening of the last one is over.
	 * `current` is the most recently started concert of today (we only know
	 * start times, so it is labelled "startet", never "pågår").
	 */
	| { phase: 'during'; current: T | null; next: T | null }
	/** after the last concert's evening */
	| { phase: 'after'; last: T }

/** Where the festival is right now, relative to its concerts. */
export function getFestivalPhase<T extends Timed>(
	events: T[],
	now: Date,
): FestivalPhase<T> {
	if (!events.length) return { phase: 'empty' }
	const sorted = [...events].sort((a, b) => time(a.date) - time(b.date))
	const first = sorted[0]!
	const last = sorted.at(-1)!
	const nowMs = now.getTime()

	if (nowMs < time(first.date)) return { phase: 'before', first, next: first }
	if (nowMs >= startOfNextFestivalDay(last.date).getTime()) {
		return { phase: 'after', last }
	}

	const next = sorted.find((event) => time(event.date) > nowMs) ?? null
	const started = sorted.filter((event) => time(event.date) <= nowMs)
	const latest = started.at(-1) ?? null
	// a concert counts as "current" only on its own evening
	const current =
		latest && nowMs < startOfNextFestivalDay(latest.date).getTime()
			? latest
			: null
	return { phase: 'during', current, next }
}

/** The first concert that has not started yet, or null. */
export function getNextEvent<T extends Timed>(events: T[], now: Date) {
	return (
		[...events]
			.sort((a, b) => time(a.date) - time(b.date))
			.find((event) => time(event.date) > now.getTime()) ?? null
	)
}

export type Countdown = {
	days: number
	hours: number
	minutes: number
	seconds: number
	/** total milliseconds left, never negative */
	total: number
}

export function getCountdown(target: Date | string, now: Date): Countdown {
	const total = Math.max(0, time(target) - now.getTime())
	return {
		days: Math.floor(total / 86_400_000),
		hours: Math.floor(total / 3_600_000) % 24,
		minutes: Math.floor(total / 60_000) % 60,
		seconds: Math.floor(total / 1_000) % 60,
		total,
	}
}

/** "om 3 dagar", "om 2 t 5 min", "om 12 min", "no" — short relative time. */
export function formatRelativeStart(target: Date | string, now: Date) {
	const { days, hours, minutes, total } = getCountdown(target, now)
	if (total < 60_000) return 'no'
	if (days >= 2) return `om ${days} dagar`
	if (days === 1) return `om 1 dag og ${hours} t`
	if (hours > 0) return `om ${hours} t ${minutes} min`
	return `om ${minutes} min`
}
